import { parse, isValid } from "date-fns";

/**
 * Parses a date string coming from `form_values` back into a `Date`.
 *
 * Two formats can coexist in the backend payload:
 *  - Legacy (AntD era): ISO-8601 with timezone, e.g. `"2000-03-12T06:00:00.000Z"`.
 *  - Modern (current):  plain calendar date,   e.g. `"2005-04-08"`.
 *
 * The calendar-day portion of the ISO string is extracted with a regex
 * instead of calling `new Date(iso)` so we don't shift the day by the
 * client's timezone offset. For Mexican birth dates serialized as
 * `T06:00:00.000Z` (UTC-6 midnight), clients in other timezones would
 * otherwise render the day before.
 */
export function parseFormDate(
  value: string | Date | null | undefined,
): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return isValid(value) ? value : undefined;

  const plainDateMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(value);
  const legacyIsoMatch = /^(\d{4}-\d{2}-\d{2})T/.exec(value);
  if (!plainDateMatch && !legacyIsoMatch) return undefined;
  if (legacyIsoMatch && !isValid(new Date(value))) return undefined;

  const datePart = plainDateMatch?.[1] ?? legacyIsoMatch?.[1];
  if (!datePart) return undefined;

  const parsed = parse(datePart, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : undefined;
}
